/**
 * AMC appraisal payment links: the pure rules behind Mercury capture, the
 * Resend inbound handler, manual entry and the borrower reminder.
 *
 * Imports only other import-free modules, so Deno and Vitest both load it.
 *
 * Trust: an emailed link is captured when the single From address belongs to an
 * AMC and the link is on that AMC's exact payment host and path. A generic page
 * is stored as the page itself, so an email can only supply its references.
 * Whether Resend's receiving server verified the sender's own signature (aligned
 * DKIM or DMARC pass) decides only binding: a verified email binds itself to the
 * loan its address names; an unverified one waits for an admin. SoCal Direct's
 * mail is sent by its ValuTrac platform and never verifies. Payment URLs are
 * bearer links: never fetch them, and never log more than their host.
 */
import { normalizeName } from './gbp-review-matching.ts';

export const APPRAISAL_PAYMENT_LINKS_FLAG = 'appraisal_payment_links_v1';
export const APPRAISAL_PAYMENT_REMINDER_TYPE = 'appraisal_payment_link_reminder';
export const APPRAISAL_PAYMENT_FROM_MAILBOX = 'notifications@lendwisemtg.com';

export interface AmcPaymentLinkRule { host: string; pathPrefix: string; generic?: boolean }
export interface AmcPaymentSource { name: string; senderDomains: readonly string[]; links: readonly AmcPaymentLinkRule[] }

/**
 * Exact payment pages from real AMC emails. Add an AMC only from a verified
 * sample: its sending domain, and the host and path its payment links use.
 * A generic page serves every order, so it also needs the order's references.
 */
export const AMC_PAYMENT_SOURCES: readonly AmcPaymentSource[] = [
  { name: 'SoCal Direct', senderDomains: ['socaldirect.com'], links: [{ host: 'socaldirect.myvalutrac.com', pathPrefix: '/payment/', generic: true }] },
  { name: 'AmeriMac', senderDomains: ['valuelinkams.com'], links: [{ host: 'amerimacamc.spurams.com', pathPrefix: '/ReceivePaymentByEmail.aspx' }] },
  // Mercury Network relays HomeBase's order messages from corelogic.com.
  { name: 'HomeBase', senderDomains: ['corelogic.com', 'homebaseamc.com'],
    links: [{ host: 'beta.homebaseamc.com', pathPrefix: '/pay/' }, { host: 'www.homebaseamc.com', pathPrefix: '/pay/' }] }
];

export interface PaymentReference { fileId?: string; orderReferenceId?: string; orderNumber?: string }
export interface PaymentLink { amcName: string; url: string; host: string; generic: boolean }
export interface CapturedPaymentLink extends PaymentLink { refKey: string }

const EMAIL = /^[^\s@<>()",;:]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

export function isDeliverableEmail(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 254 && EMAIL.test(value);
}

/** https only, with a named host, no credentials and the default port. */
export function safeHttpsUrl(raw: unknown): URL | null {
  if (typeof raw !== 'string') { return null; }
  const value = raw.trim();
  if (!value || value.length > 2048 || /\s/.test(value)) { return null; }
  let url: URL;
  try { url = new URL(value); } catch { return null; }
  if (url.protocol !== 'https:' || url.username || url.password || url.port) { return null; }
  const host = url.hostname;
  if (!host.includes('.') || host.startsWith('[') || /^[\d.]+$/.test(host)) { return null; }
  return url;
}

/**
 * The link when it is on one of an AMC's exact payment pages. A request-specific URL is kept verbatim; a generic
 * page serves every order, so it is stored as the page itself and nothing an email appends to it survives.
 */
export function matchPaymentLink(raw: string, sources: readonly AmcPaymentSource[] = AMC_PAYMENT_SOURCES): PaymentLink | null {
  const url = safeHttpsUrl(raw);
  if (!url) { return null; }
  const path = url.pathname.toLowerCase();
  for (const source of sources) {
    const rule = source.links.find((link) => link.host === url.hostname && path.startsWith(link.pathPrefix.toLowerCase()));
    if (rule) {
      return { amcName: source.name, url: rule.generic ? `https://${rule.host}${rule.pathPrefix}` : raw.trim(), host: url.hostname, generic: Boolean(rule.generic) };
    }
  }
  return null;
}

/** The references a generic payment page asks for, plus the AMC's order number when stated. */
export function paymentReference(text: string, subject = ''): PaymentReference {
  const reference: PaymentReference = {};
  const fileId = text.match(/\bFile ID:\s*([A-Za-z0-9-]{3,40})/i);
  const orderReferenceId = text.match(/\bOrder Reference ID:\s*([A-Za-z0-9-]{3,40})/i);
  const orderNumber = `${subject}\n${text}`.match(/\bYour Order:\s*([A-Za-z0-9-]{3,40})/i);
  if (fileId) { reference.fileId = fileId[1]; }
  if (orderReferenceId) { reference.orderReferenceId = orderReferenceId[1]; }
  if (orderNumber) { reference.orderNumber = orderNumber[1]; }
  return reference;
}

/** Request identity beyond the loan and URL: '' for request-specific links; both ids for a generic page. */
export function paymentRefKey(link: Pick<PaymentLink, 'generic'>, reference: PaymentReference): string | null {
  if (!link.generic) { return ''; }
  const fileId = reference.fileId?.trim().toUpperCase();
  const orderReferenceId = reference.orderReferenceId?.trim().toUpperCase();
  return fileId && orderReferenceId ? `file:${fileId}|order:${orderReferenceId}` : null;
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

function decodeEntities(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity: string) => {
    if (entity[0] === '#') {
      const code = entity[1] === 'x' || entity[1] === 'X' ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : match;
    }
    return ENTITIES[entity.toLowerCase()] ?? match;
  });
}

/** Visible text of an HTML body: block ends become line breaks, tags are dropped. */
export function htmlText(html: string | null | undefined): string {
  return decodeEntities((html || '')
    .replace(/<(script|style)\b[\s\S]*?<\/\1\s*>/gi, ' ')
    .replace(/<br\s*\/?>|<\/(p|div|tr|li|h[1-6]|table)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, ' '))
    .replace(/[ \t\f\v]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .trim();
}

/**
 * Every link in an email: anchor targets (for "click here" links) and bare URLs in the text. Plain-text parts are
 * often hard-wrapped (HomeBase's at 72 columns). Drop a bare fragment only when the following wrapped lines reconstruct
 * another whole link in the message. A shared prefix alone does not prove wrapping. Anchor targets are always kept.
 */
export function emailLinks(html: string | null | undefined, text: string | null | undefined): string[] {
  const anchors = new Set<string>();
  for (const match of (html || '').matchAll(/\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi)) {
    anchors.add(decodeEntities(match[1] ?? match[2] ?? match[3] ?? '').trim());
  }
  const body = `${text || ''}\n${htmlText(html)}`;
  const matches = [...body.matchAll(/https:\/\/[^\s"'<>]+/gi)];
  const withoutPunctuation = (url: string) => url.replace(/[.,;:!?)\]]+$/, '');
  const all = [...anchors, ...matches.map((match) => withoutPunctuation(match[0]))];
  const links = new Set(anchors);
  for (const match of matches) {
    let continued = match[0];
    let rest = body.slice(match.index! + match[0].length);
    let wrapped = false;
    while (all.some((other) => other.length > continued.length && other.startsWith(continued))) {
      const next = rest.match(/^(?:\r\n?|\n)[ \t]*([^\s"'<>]+)/);
      if (!next) { break; }
      continued += next[1];
      const whole = withoutPunctuation(continued);
      if (whole.length > match[0].length && all.includes(whole)) { wrapped = true; break; }
      rest = rest.slice(next[0].length);
    }
    if (!wrapped) { links.add(withoutPunctuation(match[0])); }
  }
  return [...links].filter(Boolean);
}

/** The property line an AMC names ("property located at:", "Property Address:"), through its ZIP. */
export function emailPropertyAddress(text: string): string | null {
  const match = text.match(/(?:property located at|property\s*address)\s*:?\s*([\s\S]{4,200}?\b\d{5}(?:-\d{4})?)(?![\d-])/i);
  return match ? match[1].replace(/\s+/g, ' ').trim() : null;
}

/** The borrower an AMC names ("Borrower:" or the "Dear …," greeting). */
export function emailBorrowerName(text: string): string | null {
  const match = text.match(/\bBorrower:[ \t]*([^\n]{2,80})/i) || text.match(/^[ \t]*Dear[ \t]+([^,\n]{2,80}),/im);
  return match ? match[1].trim() : null;
}

/** The one address in a From header, or null when it is missing or names several. */
export function singleFromAddress(from: unknown): string | null {
  if (typeof from !== 'string') { return null; }
  // A quoted display name can contain anything, including an address. Only the mailbox counts.
  const unquoted = from.replace(/"(?:[^"\\]|\\.)*"/g, ' ');
  const addresses = new Set((unquoted.match(/[^\s<>@,;:"()]+@[^\s<>@,;:"()]+/g) || []).map((address) => address.toLowerCase()));
  if (addresses.size !== 1) { return null; }
  const [address] = addresses;
  return isDeliverableEmail(address) ? address : null;
}

export interface ReceivedAuthentication { spf?: string | null; dkim?: string | null; dmarc?: string | null }

/** Resend's receiver-computed result. DKIM "pass" means the signing domain matched From; "gray" never counts. */
export function senderVerified(authentication: ReceivedAuthentication | null | undefined): boolean {
  return authentication?.dkim === 'pass' || authentication?.dmarc === 'pass';
}

export function normalizeMessageId(value: unknown): string | null {
  if (typeof value !== 'string') { return null; }
  const id = value.trim().replace(/^<|>$/g, '').trim();
  return id && id.length <= 998 && !/\s/.test(id) ? id.toLowerCase() : null;
}

export interface ReceivedAmcEmail {
  from: string;
  subject?: string | null;
  html?: string | null;
  text?: string | null;
  message_id?: string | null;
  authentication?: ReceivedAuthentication | null;
}

export type ParsedAmcEmail =
  | {
      ok: true;
      sender: string;
      /** The AMC's own signature verified. Only a verified email binds itself to a loan. */
      verified: boolean;
      links: CapturedPaymentLink[];
      reference: PaymentReference;
      propertyAddress: string | null;
      borrowerName: string | null;
      rfcMessageId: string | null;
    }
  | { ok: false; reason: 'unknown_sender' | 'no_payment_link' | 'missing_reference' };

/** Reads one received AMC email. A rejection is permanent: replaying the same email gives the same answer. */
export function parseAmcPaymentEmail(email: ReceivedAmcEmail, sources: readonly AmcPaymentSource[] = AMC_PAYMENT_SOURCES): ParsedAmcEmail {
  const sender = singleFromAddress(email.from);
  const domain = sender ? sender.slice(sender.lastIndexOf('@') + 1) : '';
  const source = sources.find((candidate) => candidate.senderDomains.includes(domain));
  if (!sender || !source) { return { ok: false, reason: 'unknown_sender' }; }
  const text = `${email.text || ''}\n${htmlText(email.html)}`;
  const reference = paymentReference(text, email.subject || '');
  const seen = new Set<string>();
  let missingReference = false;
  const links: CapturedPaymentLink[] = [];
  for (const raw of emailLinks(email.html, email.text)) {
    const link = matchPaymentLink(raw, [source]);
    if (!link || seen.has(link.url)) { continue; }
    seen.add(link.url);
    const refKey = paymentRefKey(link, reference);
    if (refKey === null) { missingReference = true; continue; }
    links.push({ ...link, refKey });
  }
  if (!links.length) { return { ok: false, reason: missingReference ? 'missing_reference' : 'no_payment_link' }; }
  return {
    ok: true, sender, verified: senderVerified(email.authentication), links, reference,
    propertyAddress: emailPropertyAddress(text), borrowerName: emailBorrowerName(text),
    rfcMessageId: normalizeMessageId(email.message_id)
  };
}

/** Payment links an AMC posted in a Mercury order message, with the references a generic page needs. The order binds the loan. */
export function mercuryPaymentLinks(text: string | null | undefined, comments: string | null | undefined): { reference: PaymentReference; links: CapturedPaymentLink[] } {
  const reference = paymentReference(`${text || ''}\n${comments || ''}`);
  const seen = new Set<string>();
  const links = emailLinks(null, `${text || ''}\n${comments || ''}`).flatMap((raw) => {
    const link = matchPaymentLink(raw);
    const refKey = link ? paymentRefKey(link, reference) : null;
    if (!link || refKey === null || seen.has(link.url)) { return []; }
    seen.add(link.url);
    return [{ ...link, refKey }];
  });
  return { reference, links };
}

/** A PostgREST `or` filter for stored Mercury messages that name any allowlisted payment host, so a replay reads every message the parser could accept. */
export function mercuryPaymentFilter(sources: readonly AmcPaymentSource[] = AMC_PAYMENT_SOURCES): string {
  const hosts = new Set(sources.flatMap((source) => source.links.map((link) => link.host)));
  return [...hosts].flatMap((host) => [`text.ilike.%${host}%`, `comments.ilike.%${host}%`]).join(',');
}

/** The bare mailboxes in header-style entries ("Name <a@b>", "a@b"), lowercased. */
export function mailboxes(entries: unknown): string[] {
  if (!Array.isArray(entries)) { return []; }
  return entries.flatMap((entry) => {
    const mailbox = singleFromAddress(entry);
    return mailbox ? [mailbox] : [];
  });
}

// ---------------------------------------------------------------------------
// Loan binding: automatic only when certain.
// ---------------------------------------------------------------------------

const STREET_WORDS: Record<string, string> = {
  STREET: 'ST', AVENUE: 'AVE', AV: 'AVE', ROAD: 'RD', DRIVE: 'DR', LANE: 'LN', COURT: 'CT', CIRCLE: 'CIR',
  BOULEVARD: 'BLVD', PLACE: 'PL', TERRACE: 'TER', PARKWAY: 'PKWY', HIGHWAY: 'HWY', TRAIL: 'TRL', SQUARE: 'SQ',
  NORTH: 'N', SOUTH: 'S', EAST: 'E', WEST: 'W', NORTHEAST: 'NE', NORTHWEST: 'NW', SOUTHEAST: 'SE', SOUTHWEST: 'SW',
  APARTMENT: 'UNIT', APT: 'UNIT', SUITE: 'UNIT', STE: 'UNIT',
  // City words that real data abbreviates either way ("Mount Dora" in Encompass, "Mt Dora" from the AMC).
  MOUNT: 'MT', SAINT: 'ST', FORT: 'FT'
};

/**
 * Conservative address tokens: case, punctuation and common suffix, directional,
 * unit and city words are normalized the same way on both sides. House numbers
 * and units keep their hyphens, so "12-14" never becomes "1214".
 */
export function addressTokens(value: string): string[] {
  return value.toUpperCase().replace(/#/g, ' UNIT ').replace(/[.,;:]/g, ' ').split(/\s+/)
    .filter(Boolean).map((token) => STREET_WORDS[token] ?? token);
}

export interface PaymentLoanCandidate {
  id: string;
  property_address: string | null;
  subject_property_city: string | null;
  subject_property_state: string | null;
  property_zip: string | null;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
  co_borrower_first_name: string | null;
  co_borrower_last_name: string | null;
}

/** The named borrower is the loan's borrower or co-borrower: first name and every last-name word. */
export function namesAgree(name: string, loan: PaymentLoanCandidate): boolean {
  const said = new Set(normalizeName(name).tokens);
  return [[loan.borrower_first_name, loan.borrower_last_name], [loan.co_borrower_first_name, loan.co_borrower_last_name]].some(([first, last]) => {
    const firstTokens = normalizeName(first).tokens;
    const lastTokens = normalizeName(last).tokens;
    return firstTokens.length > 0 && lastTokens.length > 0 && said.has(firstTokens[0]) && lastTokens.every((token) => said.has(token));
  });
}

/** The state and five-digit ZIP that end an address line, or null. */
export function addressPostal(address: string | null): { state: string; zip: string; zipIndex: number; tokens: string[] } | null {
  if (!address) { return null; }
  const tokens = addressTokens(address);
  let zipIndex = -1;
  tokens.forEach((token, index) => { if (/^\d{5}(?:-\d{4})?$/.test(token)) { zipIndex = index; } });
  if (zipIndex < 3 || !/^[A-Z]{2}$/.test(tokens[zipIndex - 1])) { return null; }
  return { state: tokens[zipIndex - 1], zip: tokens[zipIndex].slice(0, 5), zipIndex, tokens };
}

/**
 * The one eligible loan whose complete address the AMC named, or null: every
 * token of the street line (house number and unit exact) and city, then the
 * state and ZIP. Anything extra or missing ("Building B", another unit) leaves
 * the link unbound. A named borrower who is not on that loan blocks the match.
 */
export function matchPaymentLoan(address: string | null, borrowerName: string | null, loans: readonly PaymentLoanCandidate[]): string | null {
  const postal = addressPostal(address);
  if (!postal) { return null; }
  const { state, zip, zipIndex, tokens } = postal;
  const said = tokens.slice(0, zipIndex - 1);
  const matches = loans.filter((loan) => {
    if ((loan.property_zip || '').trim().slice(0, 5) !== zip || (loan.subject_property_state || '').trim().toUpperCase() !== state) { return false; }
    const street = addressTokens(loan.property_address || '');
    const city = addressTokens(loan.subject_property_city || '');
    if (street.length < 2 || !/^\d/.test(street[0]) || !city.length) { return false; }
    const expected = [...street, ...city];
    return expected.length === said.length && expected.every((token, index) => said[index] === token);
  });
  if (matches.length !== 1) { return null; }
  return borrowerName && !namesAgree(borrowerName, matches[0]) ? null : matches[0].id;
}

// ---------------------------------------------------------------------------
// The reminder envelope
// ---------------------------------------------------------------------------

export interface ReminderStaff { id: string; email: string; name: string }
/** copied: the LO is on the email; sender: the LO is sending it; not_found: no single active LO user. */
export type ReminderLoanOfficer = 'copied' | 'sender' | 'not_found';
export interface ReminderEnvelope { from: string; to: string[]; cc: string[]; bcc: string[]; replyTo: string; loanOfficer: ReminderLoanOfficer }

function displayName(name: string): string {
  return name.replace(/["\\<>\r\n\t]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
}

/**
 * Borrowers in To, the loan's LO in Cc unless the LO is the sender, the sender
 * in Bcc and Reply-To. The From mailbox is fixed; only its display name names
 * the sender.
 */
export function reminderEnvelope(input: { borrowerEmails: Iterable<string>; sender: ReminderStaff; loanOfficer: ReminderStaff | null }): ReminderEnvelope {
  const senderEmail = input.sender.email.trim().toLowerCase();
  const to = [...new Set([...input.borrowerEmails].map((email) => email.trim().toLowerCase()).filter(isDeliverableEmail))].sort();
  const loEmail = input.loanOfficer?.email.trim().toLowerCase() || '';
  const loanOfficer: ReminderLoanOfficer = !input.loanOfficer || !isDeliverableEmail(loEmail) ? 'not_found'
    : input.loanOfficer.id === input.sender.id || loEmail === senderEmail ? 'sender' : 'copied';
  const name = displayName(input.sender.name);
  return {
    from: `"${name ? `${name}, ` : ''}Lendwise Mortgage" <${APPRAISAL_PAYMENT_FROM_MAILBOX}>`,
    to,
    cc: loanOfficer === 'copied' && !to.includes(loEmail) ? [loEmail] : [],
    bcc: to.includes(senderEmail) ? [] : [senderEmail],
    replyTo: senderEmail,
    loanOfficer
  };
}

/** Canonical form for comparing the envelope a sender confirmed with the one the server would send. */
export function envelopeKey(envelope: Pick<ReminderEnvelope, 'to' | 'cc' | 'bcc' | 'replyTo'>): string {
  const list = (values: readonly string[]) => [...values].map((value) => value.trim().toLowerCase()).sort();
  return JSON.stringify({ to: list(envelope.to), cc: list(envelope.cc), bcc: list(envelope.bcc), replyTo: envelope.replyTo.trim().toLowerCase() });
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
